// The head of a record page (R-U6, decision 031 п.1): the round «назад» and the full path in crumbs, the record's title last.

import { menuPlace, recordTitle, type Id, type MenuSection, type MetaIndex, type RecordObj } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Tooltip } from '../ui/Tooltip.tsx';
import { cameFromApp, navigate, typeHref, wantsNewTab } from '../shell/router.ts';
import { listOf, sectionHref } from '../shell/nav-place.ts';
import { useScopeWords } from '../shell/scope.tsx';
import s from './record.module.css';

export interface Crumb { label: string; href?: string; title?: string }
/** More crumbs than this fold their middle into «…»: the first and the last ones say where the record is. */
const MAX_CRUMBS = 6;

/** Where the menu places a type: the rail section and the folders down to it. */
const placeOf = (menu: MenuSection[], typeId: string) => menuPlace(menu, (n) => n.typeIds.includes(typeId) || !!n.views?.some((v) => v.typeId === typeId));

/**
 * The parent of a subordinate record, when it is unambiguous: the only linked record whose section stands higher in the tree of the
 * same rail section (a line of an order → the order; a replacement points at both a ticket and an order, so it has no parent).
 */
function parentOf(rec: RecordObj, meta: MetaIndex, menu: MenuSection[], linked: (r: RecordObj, f: string) => Id[], get: (id: Id) => RecordObj | undefined): RecordObj | undefined {
  const here = placeOf(menu, rec.typeId);
  if (!here) return undefined;
  const found: Id[] = [];
  for (const f of meta.fields(rec.typeId)) {
    if (f.fieldType !== 'relation' || f.archivedAt) continue;
    const target = meta.relationTarget(f);
    if (!target || target.many || target.typeId === rec.typeId) continue;
    const there = placeOf(menu, target.typeId);
    if (!there || there.section !== here.section || there.path.length >= here.path.length) continue;
    const ids = linked(rec, f.key);
    if (ids.length === 1) found.push(ids[0]!);
  }
  return found.length === 1 ? get(found[0]!) : undefined;
}

export function useRecordCrumbs(rec: RecordObj | undefined): { crumbs: Crumb[]; list: string | null } {
  const meta = useMeta();
  const client = useSync();
  // A section in the words of the chosen event (R-E9): the tickets of a product without a date are «Доступы» here too.
  const words = useScopeWords();
  if (!rec) return { crumbs: [], list: null };
  const type = meta.type(rec.typeId);
  const place = placeOf(client.menu, rec.typeId);
  const list = (type && (listOf(type.id) ?? typeHref(type.key))) || null;
  const crumbs: Crumb[] = [];
  if (place) {
    crumbs.push({ label: place.section.label, href: sectionHref(place.section, meta) ?? undefined });
    for (const g of place.path) crumbs.push({ label: g.label });
  }
  if (type) crumbs.push({ label: (place?.section.eventScoped ? words.types.get(type.id)?.nameMany : undefined) ?? type.nameMany, href: list ?? undefined });
  const parent = parentOf(rec, meta, client.menu, (r, f) => { const fd = meta.field(r.typeId, f); return fd ? client.store.linked(r, fd) : []; }, (id) => client.store.record(id));
  if (parent) crumbs.push({ label: recordTitle(parent, meta), href: `/r/${parent.id}` });
  crumbs.push({ label: recordTitle(rec, meta) });
  return { crumbs, list };
}

/** «Назад» goes where the person came from inside the app; after a direct visit — to the list of the record's section. */
export function goBack(list: string | null) {
  if (cameFromApp()) history.back(); else navigate(list ?? '/');
}

export function RecordPath({ rec, onBack }: { rec: RecordObj | undefined; onBack: () => void }) {
  return <Path crumbs={useRecordCrumbs(rec).crumbs} onBack={onBack} />;
}

/**
 * The round «назад» and crumbs of any single page (a record, a file, a letter): the last crumb is where the person is. Without
 * `onBack` only the crumbs: the letter of the mail stands beside its list, the way back is the list itself.
 */
export function Path({ crumbs, onBack, label = 'Путь к записи' }: { crumbs: Crumb[]; onBack?: () => void; label?: string }) {
  const shown: (Crumb | 'fold')[] = crumbs.length > MAX_CRUMBS ? [crumbs[0]!, 'fold', ...crumbs.slice(-(MAX_CRUMBS - 2))] : crumbs;
  const folded = crumbs.slice(1, crumbs.length - (MAX_CRUMBS - 2)).map((c) => c.label).join(' › ');
  return (
    <div className={s.path}>
      {onBack && (
        <Tooltip label="Назад (Esc)" side="bottom">
          <button type="button" className={s.back} aria-label="Назад" onClick={onBack}><Icon name="back" size={16} strokeWidth={2} /></button>
        </Tooltip>
      )}
      <nav className={s.crumbs} aria-label={label}>
        <ol>
          {shown.map((c, i) => (
            <li key={i} className={i === shown.length - 1 ? s.crumbLast : undefined}>
              {i > 0 && <Icon name="chevronRight" size={12} className={s.crumbSep} />}
              {c === 'fold' ? <span className={s.crumbText} title={folded}>…</span>
                : c.href ? <a className={s.crumbLink} href={c.href} onClick={(e) => { if (wantsNewTab(e)) return; e.preventDefault(); navigate(c.href!); }}>{c.label}</a>
                : <span className={s.crumbText} aria-current={i === shown.length - 1 ? 'page' : undefined}>{c.label}</span>}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}
